"use client"

import { m } from "framer-motion"
import { Smartphone, Gamepad2, Shield } from "lucide-react"

const SERVICES = [
  {
    icon: Smartphone,
    title: "Troca de Tela/Bateria",
    description: "Reparos especializados em smartphones Apple e Android.",
  },
  {
    icon: Gamepad2,
    title: "Manutenção de Consoles",
    description: "Assistência técnica para PS5, Xbox e controles.",
  },
  {
    icon: Shield,
    title: "Películas e Proteção",
    description: "Instalação profissional de películas e proteção de tela.",
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut" as const,
    },
  },
}

export function Services() {
  return (
    <section 
      id="services" 
      className="py-24 bg-gradient-to-b from-[#0A0A0F] to-[#0F0F1A] relative overflow-hidden"
      style={{ contain: "layout style paint" }}
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div 
          className="absolute top-0 left-1/4 w-96 h-96 bg-[#B829FF]/10 rounded-full blur-[100px]"
          style={{ willChange: "opacity" }}
        />
        <div 
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#00A3FF]/10 rounded-full blur-[100px]"
          style={{ willChange: "opacity" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px", amount: 0.3 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ willChange: "transform, opacity" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black font-[family-name:var(--font-orbitron)] text-white mb-4">
            Soluções Especializadas
          </h2>
          <p className="text-slate-100 text-lg max-w-2xl mx-auto font-medium">
            Atendimento profissional para todas as suas necessidades em tecnologia.
          </p>
        </m.div>

        <m.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px", amount: 0.2 }}
          className="flex flex-wrap justify-center gap-8"
        >
          {SERVICES.map((service) => (
            <m.div
              key={service.title}
              variants={itemVariants}
              style={{ willChange: "transform, opacity" }}
              className="group relative w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-sm"
            >
              <div className="relative p-8 glass-cyber rounded-2xl hover:border-[#B829FF]/60 transition-colors duration-500 overflow-hidden">
                {/* Corner Brackets */}
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#B829FF] rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#B829FF] rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#B829FF] rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#B829FF] rounded-br-lg" />

                {/* Hover glow effect */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-[#B829FF]/10 via-transparent to-[#00A3FF]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                  style={{ willChange: "opacity" }}
                />

                <div className="relative flex flex-col items-center text-center">
                  <div 
                    className="w-16 h-16 bg-gradient-to-br from-[#B829FF] via-[#00A3FF] to-[#00F0FF] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 neon-box-purple"
                    style={{ willChange: "transform" }}
                  >
                    <service.icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-xl font-bold font-[family-name:var(--font-orbitron)] text-white mb-3 group-hover:text-[#00F0FF] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-slate-200 text-base leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </m.div>
          ))}
        </m.div>
      </div>
    </section>
  )
}
